import {
    getSurgerySuggestions,
    OnboardingSurgery,
    TussSuggestion,
} from '@/lib/api/onboarding/onboarding-api'
import { useEffect, useRef, useState } from 'react'
import { MdAdd, MdCheckCircle, MdClose } from 'react-icons/md'

interface Props {
    isSurgeon: boolean
    value: OnboardingSurgery[]
    /**
     * Verdadeiro quando as especialidades informadas costumam envolver cirurgia.
     * Só muda o TEXTO de apoio — a caixa continua desmarcada, porque a maioria dos
     * médicos não opera e marcar por conta própria cadastraria cirurgia em conta de
     * quem só consulta.
     */
    hinted: boolean
    onChangeIsSurgeon: (isSurgeon: boolean) => void
    onChange: (surgeries: OnboardingSurgery[]) => void
}

/**
 * Cirurgias mais realizadas, casadas com a Tabela TUSS.
 *
 * O casamento é confirmado pelo médico, não adivinhado: item cadastrado com o TUSS
 * errado vira preço errado na guia, e item sem TUSS nenhum nasce fora da
 * unificação e volta como duplicata na primeira importação. Quando nenhuma
 * sugestão serve, o item é criado com o nome livre mesmo — é melhor do que não
 * cadastrar.
 */
export default function SurgeriesStep({
    isSurgeon,
    value,
    hinted,
    onChangeIsSurgeon,
    onChange,
}: Props) {
    const [draft, setDraft] = useState('')
    const [suggestions, setSuggestions] = useState<TussSuggestion[]>([])
    const [searching, setSearching] = useState(false)
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current)
        const term = draft.trim()
        if (term.length < 3) {
            setSuggestions([])
            return
        }

        debounceRef.current = setTimeout(async () => {
            setSearching(true)
            try {
                const res = await getSurgerySuggestions(term)
                setSuggestions(res.success && Array.isArray(res.result) ? res.result : [])
            } finally {
                setSearching(false)
            }
        }, 300)

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current)
        }
    }, [draft])

    const add = (name: string, tussCode?: string) => {
        const trimmed = name.trim()
        if (!trimmed) return
        if (value.some(item => item.name.toLowerCase() === trimmed.toLowerCase())) {
            setDraft('')
            return
        }
        onChange([...value, { name: trimmed, tussCode }])
        setDraft('')
        setSuggestions([])
    }

    const remove = (index: number) => {
        onChange(value.filter((_, i) => i !== index))
    }

    return (
        <div className="flex flex-col gap-5">
            <div className="rounded-lg border border-base-300 p-4">
                <label className="label cursor-pointer justify-start gap-3 p-0">
                    <input
                        type="checkbox"
                        className="checkbox checkbox-secondary"
                        checked={isSurgeon}
                        onChange={e => onChangeIsSurgeon(e.target.checked)}
                    />
                    <span className="label-text font-medium">Eu realizo cirurgias</span>
                </label>
                {hinted && !isSurgeon && (
                    <p className="mt-2 text-sm text-base-content/60">
                        Suas especialidades costumam envolver cirurgia — marque se for o
                        seu caso.
                    </p>
                )}
            </div>

            {isSurgeon && (
                <>
                    <div className="form-control">
                        <label className="label pb-1" htmlFor="onboarding-surgery">
                            <span className="label-text font-medium">
                                Escreva as cirurgias que você mais realiza
                            </span>
                        </label>
                        <div className="flex gap-2">
                            <input
                                id="onboarding-surgery"
                                className="input input-bordered w-full"
                                value={draft}
                                onChange={e => setDraft(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault()
                                        add(draft)
                                    }
                                }}
                                placeholder="Colecistectomia videolaparoscópica"
                            />
                            <button
                                type="button"
                                className="btn btn-secondary gap-1"
                                onClick={() => add(draft)}
                                disabled={!draft.trim()}
                            >
                                <MdAdd className="text-lg" />
                                Adicionar
                            </button>
                        </div>
                        <p className="mt-1 text-xs text-base-content/60 whitespace-normal break-words">
                            {searching
                                ? 'Procurando na Tabela TUSS...'
                                : 'Escolha a sugestão da Tabela TUSS quando houver — é o que mantém o item padronizado.'}
                        </p>
                    </div>

                    {suggestions.length > 0 && (
                        <ul className="flex flex-col gap-2">
                            {suggestions.map(suggestion => (
                                <li key={suggestion.tussCode}>
                                    <button
                                        type="button"
                                        className="flex w-full items-center justify-between gap-3 rounded-lg border border-base-300 px-4 py-3 text-left hover:border-secondary"
                                        onClick={() => add(suggestion.term, suggestion.tussCode)}
                                    >
                                        <span className="text-sm">{suggestion.term}</span>
                                        <span className="badge badge-ghost whitespace-nowrap">
                                            TUSS {suggestion.tussCode}
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}

                    {value.length > 0 && (
                        <ul className="flex flex-col gap-2">
                            {value.map((surgery, index) => (
                                <li
                                    key={`${surgery.name}-${index}`}
                                    className="flex items-center justify-between gap-3 rounded-lg border border-base-300 px-4 py-3"
                                >
                                    <div className="flex items-center gap-2">
                                        {surgery.tussCode ? (
                                            <MdCheckCircle className="text-lg text-success" />
                                        ) : (
                                            <span className="badge badge-warning badge-sm">
                                                sem TUSS
                                            </span>
                                        )}
                                        <span className="text-sm">{surgery.name}</span>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        {surgery.tussCode && (
                                            <span className="badge badge-ghost whitespace-nowrap">
                                                TUSS {surgery.tussCode}
                                            </span>
                                        )}
                                        <button
                                            type="button"
                                            className="btn btn-ghost btn-sm btn-circle"
                                            onClick={() => remove(index)}
                                            aria-label={`Remover ${surgery.name}`}
                                        >
                                            <MdClose className="text-lg" />
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}

                    <div className="alert alert-info text-sm">
                        <span>
                            As cirurgias entram no catálogo sem preço: cirurgia se negocia
                            caso a caso, e o valor é definido em cada agendamento.
                        </span>
                    </div>
                </>
            )}
        </div>
    )
}
